import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell, fetchWidget } from "./base";

type SteamWidget = Extract<ProfileConfig["widgets"][number], { type: "steam" }>;

type GameRow = {
  name: string;
  appId: number;
  playtimeRecent?: number;
  playtimeTotal: number;
  iconUrl: string;
};

type SteamData = {
  steamId: string;
  personaName?: string;
  realName?: string | null;
  avatarUrl?: string;
  avatarFullUrl?: string;
  profileUrl?: string;
  countryCode?: string | null;
  state?: number;
  playing?: { name: string; appId: string } | null;
  createdAt?: number | null;
  recentGame?: GameRow | null;
  recentGames?: GameRow[];
  topGames?: GameRow[];
  steamLevel?: number | null;
  gameCount?: number | null;
  unconfigured?: boolean;
  invalidKey?: boolean;
};

const STATE_LABEL: Record<number, string> = {
  0: "offline",
  1: "online",
  2: "busy",
  3: "away",
  4: "snooze",
  5: "looking to trade",
  6: "looking to play",
};

const STATE_CLASS: Record<number, string> = {
  0: "off",
  1: "on",
  2: "dnd",
  3: "away",
  4: "away",
  5: "on",
  6: "on",
};

function formatPlaytime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.round(minutes / 60);
  if (h < 1000) return `${h}h`;
  return `${(h / 1000).toFixed(1)}kh`;
}

function gameRow(g: GameRow, recent: boolean): HTMLElement {
  const row = el("div", { class: "widget-steam__game" });
  if (g.iconUrl) {
    row.append(
      el("img", {
        class: "widget-steam__game-icon",
        src: g.iconUrl,
        alt: "",
        draggable: false,
      }),
    );
  }
  const info = el("div", { class: "widget-steam__game-info" });
  info.append(el("div", { class: "widget-steam__game-name" }, [g.name]));
  const meta: string[] = [];
  if (recent && g.playtimeRecent && g.playtimeRecent > 0) {
    meta.push(`${formatPlaytime(g.playtimeRecent)} past 2wk`);
  }
  meta.push(`${formatPlaytime(g.playtimeTotal)} total`);
  info.append(el("div", { class: "widget-steam__game-meta" }, [meta.join(" · ")]));
  row.append(info);
  return row;
}

export function mountSteamWidget(cfg: SteamWidget): HTMLElement {
  const shell = widgetShell({ kind: "steam", title: "Steam", accent: "#66c0f4" });
  shell.setLoading();
  const controller = new AbortController();
  shell.root.addEventListener("bio:teardown", () => controller.abort());
  const hide = new Set(cfg.hide ?? []);
  const size = cfg.size ?? "md";
  shell.root.setAttribute("data-size", size);

  const render = async () => {
    const env = await fetchWidget<SteamData>(
      "steam",
      { id: cfg.steamId64 },
      { signal: controller.signal },
    );
    if (!env.ok || !env.data) return shell.setError(env.error ?? "unavailable");
    const d = env.data;
    if (d.unconfigured) return shell.setError("requires STEAM_API_KEY");
    if (d.invalidKey)
      return shell.setError("invalid STEAM_API_KEY — regenerate at steamcommunity.com/dev/apikey");

    shell.body.innerHTML = "";
    const profileHref = d.profileUrl ?? `https://steamcommunity.com/profiles/${d.steamId}`;
    const stateKey = d.playing ? 1 : (d.state ?? 0);
    const stateClass = d.playing ? "ingame" : STATE_CLASS[stateKey] ?? "off";
    const stateLabel = d.playing ? `in-game` : STATE_LABEL[stateKey] ?? "offline";

    // Top row: avatar + name + state
    const top = el("div", { class: "widget-steam__top" });
    if (d.avatarUrl && !hide.has("avatar")) {
      top.append(
        el("img", {
          class: `widget-steam__avatar widget-steam__avatar--${stateClass}`,
          src: d.avatarUrl,
          alt: "",
          draggable: false,
        }),
      );
    }
    const names = el("div", { class: "widget-steam__names" });
    names.append(el("div", { class: "widget-steam__name" }, [d.personaName ?? d.steamId]));
    const sub = el("div", { class: "widget-steam__sub" });
    if (!hide.has("level") && typeof d.steamLevel === "number" && d.steamLevel > 0) {
      sub.append(el("span", { class: "widget-steam__level" }, [`Lv ${d.steamLevel}`]));
    }
    sub.append(
      el("span", { class: `widget-steam__status widget-steam__status--${stateClass}` }, [stateLabel]),
    );
    names.append(sub);
    top.append(names);
    shell.body.append(top);

    // sm: top row + single compact recent/in-game line.
    // md: top + recent-game card + meta line + button.
    // lg: tabbed game list + meta + button.
    if (size === "sm") {
      if (!hide.has("recentGame")) {
        if (d.playing) {
          shell.body.append(
            el("div", { class: "widget-steam__game widget-steam__game--live" }, [
              el("span", { class: "widget-steam__game-name" }, [
                `Playing ${d.playing.name}`,
              ]),
            ]),
          );
        } else if (d.recentGame) {
          shell.body.append(gameRow(d.recentGame, true));
        }
      }
    } else {
      if (!hide.has("recentGame")) {
        if (d.playing) {
          shell.body.append(
            el("div", { class: "widget-steam__game widget-steam__game--live" }, [
              el("span", { class: "widget-steam__game-lbl" }, ["Playing "]),
              el("span", { class: "widget-steam__game-name" }, [d.playing.name]),
            ]),
          );
        } else if (size === "lg") {
          // Tabbed: Recent / All-time
          const tabs = el("div", { class: "widget-steam__tabs", role: "tablist" });
          const recent = d.recentGames ?? [];
          const top5 = d.topGames ?? [];
          const listWrap = el("div", { class: "widget-steam__games" });

          const renderList = (which: "recent" | "top") => {
            listWrap.innerHTML = "";
            const rows = which === "recent" ? recent : top5;
            if (rows.length === 0) {
              listWrap.append(
                el("div", { class: "widget-steam__games-empty" }, [
                  which === "recent" ? "no recent games" : "no owned games found",
                ]),
              );
            } else {
              for (const g of rows) listWrap.append(gameRow(g, which === "recent"));
            }
          };

          const mkTab = (key: "recent" | "top", label: string) => {
            const btn = el("button", {
              type: "button",
              class: "widget-steam__tab",
              role: "tab",
              "data-key": key,
            }, [label]);
            btn.addEventListener("click", () => {
              tabs.querySelectorAll(".widget-steam__tab").forEach((t) => t.classList.remove("is-active"));
              btn.classList.add("is-active");
              renderList(key);
            });
            return btn;
          };
          const tabRecent = mkTab("recent", "Recent");
          const tabTop = mkTab("top", "All-time");
          tabRecent.classList.add("is-active");
          tabs.append(tabRecent, tabTop);
          shell.body.append(tabs, listWrap);
          renderList("recent");
        } else if (d.recentGame) {
          shell.body.append(gameRow(d.recentGame, true));
        }
      }

      if (!hide.has("meta")) {
        const metaBits: string[] = [];
        if (d.realName) metaBits.push(d.realName);
        if (d.countryCode) metaBits.push(d.countryCode);
        if (typeof d.gameCount === "number" && d.gameCount > 0) {
          metaBits.push(`${d.gameCount.toLocaleString()} games`);
        }
        if (d.createdAt) {
          const year = new Date(d.createdAt * 1000).getUTCFullYear();
          metaBits.push(`member since ${year}`);
        }
        if (metaBits.length > 0) {
          shell.body.append(el("div", { class: "widget-steam__meta" }, [metaBits.join(" · ")]));
        }
      }

      if (!hide.has("button")) {
        shell.body.append(
          el(
            "a",
            {
              class: "widget-steam__btn",
              href: profileHref,
              target: "_blank",
              rel: "noopener noreferrer",
            },
            ["View profile"],
          ),
        );
      }
    }
  };

  void render();
  const interval = window.setInterval(render, 2 * 60_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
